import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Input, InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Text } from "@amzn/stencil-react-components/text";
import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { updateOnboardingStep } from "@/apis/UserOnboardingStepHelper";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { useCacheDomainCredentials } from "@/hooks/useCacheDomainCredentials";

import { ActionType, AppContext } from "../../stores";
import { STEP_ID } from "../constants";

/**
 * A step that caches the new hire's ANT domain credentials on their Windows
 * laptop so they can sign in with their Amazon password without first
 * connecting to VPN after a restart. This is the Windows parallel to the Mac
 * Pre-Cache AD step and only renders when the Electron credential-caching hook
 * is available (see getEnabledSteps).
 */
export default function AmazonAccountCaching(): JSX.Element {
  const intl = useIntl();
  const { state, dispatch } = useContext(AppContext);
  const { amazonUsername } = state;
  const [password, setPassword] = useState<string>("");
  const [hasAttempted, setHasAttempted] = useState<boolean>(false);
  const { cacheCredentials, isLoading, isSuccess, error } =
    useCacheDomainCredentials();

  const regularFontWeight = "regular";

  /**
   * Set the onboarding step and the refPage to provide context to IT Support
   * should the new hire initiate a chat with support. The onboarding step value
   * is shared with the Mac Pre-Cache AD step since it represents the same
   * concept (account caching).
   */
  useEffect(() => {
    updateOnboardingStep("D1_PRECACHE_AD");
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-amazon-account-cache",
    });
  }, []);

  /**
   * Cache the new hire's domain credentials. On failure we surface a warning
   * and offer a secondary "Continue without caching" button. On success we
   * hide the form and show a success banner with a Next button.
   */
  const handleCache = async (): Promise<void> => {
    setHasAttempted(true);
    const succeeded = await cacheCredentials(amazonUsername ?? "", password);

    if (succeeded) {
      setPassword("");
    }
  };

  const handleNext = (): void => {
    dispatch({
      type: ActionType.NEXT_STEP,
      stepId: STEP_ID.AMAZON_ACCOUNT_CACHING,
    });
  };

  const isCacheDisabled = isLoading || password.length === 0;

  // After a successful cache, show only the success banner and Next button.
  if (isSuccess) {
    return (
      <Col gridGap="S400">
        <MessageBanner
          dataTestId="amazonAccountCachingSuccess"
          type={MessageBannerType.Success}
        >
          <FormattedMessage id="amazonAccountCaching-success" />
        </MessageBanner>

        <Row>
          <Button
            variant={ButtonVariant.Primary}
            onClick={handleNext}
            dataTestId="amazonAccountCachingNextBtn"
          >
            <FormattedMessage id="button-next" />
          </Button>
        </Row>
      </Col>
    );
  }

  return (
    <Col gridGap="S400">
      <StepBodyStyledText>
        <Text
          fontWeight={regularFontWeight}
          dataTestId="amazonAccountCachingIntro"
        >
          <FormattedMessage id="amazonAccountCaching-expander-body-intro" />
        </Text>
      </StepBodyStyledText>

      <View maxWidth="400px" width="100%">
        <InputWrapper
          id="amazonAccountCachingPasswordInput"
          labelText={intl.formatMessage({
            id: "amazonAccountCaching-password-label",
          })}
          dataTestId="amazonAccountCachingPasswordWrapper"
        >
          {({ id, ...inputProps }) => (
            <Input
              {...inputProps}
              id={id}
              type="password"
              value={password}
              onChange={(evt) => setPassword(evt.target.value)}
              error={!!error}
              disabled={isLoading}
              dataTestId="amazonAccountCachingPasswordInput"
            />
          )}
        </InputWrapper>
      </View>

      {error && (
        <MessageBanner
          dataTestId="amazonAccountCachingWarning"
          type={MessageBannerType.Warning}
        >
          <FormattedMessage id="amazonAccountCaching-error" />
        </MessageBanner>
      )}

      <Row columnGap="S300">
        <Button
          variant={ButtonVariant.Primary}
          dataTestId="amazonAccountCachingCacheBtn"
          disabled={isCacheDisabled}
          onClick={() => void handleCache()}
        >
          <Text fontWeight="bold">
            <FormattedMessage
              id={
                isLoading
                  ? "amazonAccountCaching-button-caching"
                  : "amazonAccountCaching-button-cache"
              }
            />
          </Text>
        </Button>

        {hasAttempted && error && (
          <Button
            variant={ButtonVariant.Tertiary}
            dataTestId="amazonAccountCachingSkipBtn"
            onClick={handleNext}
          >
            <FormattedMessage id="amazonAccountCaching-button-skip" />
          </Button>
        )}
      </Row>
    </Col>
  );
}
